/**
 * Inline SVG icon set: 24 px grid, 1.75 stroke, round caps and joins,
 * `currentColor`. Icons are decorative by default (`aria-hidden`); pass a
 * `title` when the icon is the only content and must be announced.
 */
import type { SVGProps } from 'react';

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  /** Pixel size; default 24. */
  size?: number;
  /** Accessible name; when omitted the icon is aria-hidden. */
  title?: string;
}

export type IconComponent = (props: IconProps) => React.JSX.Element;

function make(name: string, paths: string[], opts: { fill?: string[] } = {}): IconComponent {
  const Icon: IconComponent = ({ size = 24, title, className, ...rest }) => (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className ? `icon icon-${name} ${className}` : `icon icon-${name}`}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      focusable="false"
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      {paths.map((d, i) => (
        <path key={i} d={d} />
      ))}
      {opts.fill?.map((d, i) => (
        <path key={`f${i}`} d={d} fill="currentColor" stroke="none" />
      ))}
    </svg>
  );
  (Icon as { displayName?: string }).displayName = `Icon${name[0]!.toUpperCase()}${name.slice(1)}`;
  return Icon;
}

// Navigation
export const HomeIcon = make('home', ['M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-4.5v-6h-5v6H5a1 1 0 0 1-1-1z']);
export const InboxIcon = make('inbox', ['M4 5h16v14H4z', 'M4 14h4.5l1.5 2.5h4l1.5-2.5H20']);
export const LedgerIcon = make('ledger', ['M5 4h14v16H5z', 'M8.5 8.5h7', 'M8.5 12h7', 'M8.5 15.5h4']);
export const ChartIcon = make('chart', ['M4 20h16', 'M7 16v-5', 'M12 16V6', 'M17 16v-8']);
export const MoreIcon = make('more', [], { fill: ['M6 10.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3zm6 0a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3zm6 0a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3z'] });
export const PlusIcon = make('plus', ['M12 5v14', 'M5 12h14']);
export const MinusIcon = make('minus', ['M5 12h14']);
export const MenuIcon = make('menu', ['M4 7h16', 'M4 12h16', 'M4 17h16']);

// Chevrons & arrows
export const ChevronDownIcon = make('chevron-down', ['m6 9 6 6 6-6']);
export const ChevronUpIcon = make('chevron-up', ['m6 15 6-6 6 6']);
export const ChevronLeftIcon = make('chevron-left', ['m15 6-6 6 6 6']);
export const ChevronRightIcon = make('chevron-right', ['m9 6 6 6-6 6']);
export const ArrowLeftIcon = make('arrow-left', ['M19 12H5', 'm11 6-6 6 6 6']);
export const ArrowRightIcon = make('arrow-right', ['M5 12h14', 'm13 6 6 6-6 6']);
export const ArrowUpRightIcon = make('arrow-up-right', ['M7 17 17 7', 'M9 7h8v8']);
export const ArrowDownLeftIcon = make('arrow-down-left', ['M17 7 7 17', 'M15 17H7V9']);
export const ArrowLeftRightIcon = make('arrow-left-right', ['M4 8h16', 'm16 4 4 4-4 4', 'M20 16H4', 'm8 12-4 4 4 4']);
export const ExternalLinkIcon = make('external-link', ['M14 4h6v6', 'M20 4 11 13', 'M18 13.5V19a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5.5']);

// Status
export const CheckIcon = make('check', ['m5 12.5 4.5 4.5L19 7']);
export const CheckCircleIcon = make('check-circle', ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z', 'm8.5 12.5 2.5 2.5 4.5-5']);
export const XIcon = make('x', ['M6 6l12 12', 'M18 6 6 18']);
export const AlertIcon = make('alert', ['M12 4 2.8 20h18.4z', 'M12 10v4', 'M12 17.5v.01']);
export const InfoIcon = make('info', ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z', 'M12 11v5', 'M12 8v.01']);
export const QuestionIcon = make('question', ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z', 'M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.3-1 .9-1 1.7', 'M12 17v.01']);
export const ClockIcon = make('clock', ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z', 'M12 7v5l3 2']);
export const LockIcon = make('lock', ['M6 11h12v9H6z', 'M8.5 11V8a3.5 3.5 0 0 1 7 0v3']);
export const UnlockIcon = make('unlock', ['M6 11h12v9H6z', 'M8.5 11V8a3.5 3.5 0 0 1 6.7-1.4']);
export const SparkleIcon = make('sparkle', ['M12 3v4', 'M12 17v4', 'M3 12h4', 'M17 12h4', 'm12 7 1.5 3.5L17 12l-3.5 1.5L12 17l-1.5-3.5L7 12l3.5-1.5z']);
export const CloudIcon = make('cloud', ['M7 18a4 4 0 0 1-.5-8 5.5 5.5 0 0 1 10.6 1.5A3.3 3.3 0 0 1 17 18z']);
export const CloudOffIcon = make('cloud-off', ['M7 18a4 4 0 0 1-.5-8 5.5 5.5 0 0 1 1.3-2.7', 'M10.5 6.4A5.5 5.5 0 0 1 17.1 11.5 3.3 3.3 0 0 1 17 18h-6', 'M4 4l16 16']);
export const RefreshIcon = make('refresh', ['M20 11a8 8 0 0 0-14.5-3.5', 'M4 4v4.5h4.5', 'M4 13a8 8 0 0 0 14.5 3.5', 'M20 20v-4.5h-4.5']);
export const SpinnerIcon = make('spinner', ['M12 3a9 9 0 0 1 9 9']);

// Objects
export const CameraIcon = make('camera', ['M4 8h3.5l1.5-2.5h6L16.5 8H20v11H4z', 'M12 16.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z']);
export const UploadIcon = make('upload', ['M12 16V4', 'm7 9 5-5 5 5', 'M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3']);
export const DownloadIcon = make('download', ['M12 4v12', 'm7 11 5 5 5-5', 'M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3']);
export const ReceiptIcon = make('receipt', ['M6 3h12v18l-2-1.5L14 21l-2-1.5L10 21l-2-1.5L6 21z', 'M9 8h6', 'M9 11.5h6', 'M9 15h3.5']);
export const BankIcon = make('bank', ['M3 9.5 12 4l9 5.5H3z', 'M5 9.5V18', 'M9.5 9.5V18', 'M14.5 9.5V18', 'M19 9.5V18', 'M3 20h18']);
export const BuildingIcon = make('building', ['M5 21V4h10v17', 'M15 9h4v12', 'M8 8h1', 'M11 8h1', 'M8 12h1', 'M11 12h1', 'M8 16h1', 'M11 16h1', 'M3 21h18']);
export const CardIcon = make('card', ['M3 6h18v12H3z', 'M3 10h18', 'M7 15h3']);
export const CashIcon = make('cash', ['M3 7h18v10H3z', 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z', 'M6.5 12h.01', 'M17.5 12h.01']);
export const WalletIcon = make('wallet', ['M4 7a2 2 0 0 1 2-2h11v3', 'M4 7v11a2 2 0 0 0 2 2h14V9H6a2 2 0 0 1-2-2z', 'M16 14h.01']);
export const PhoneIcon = make('phone', ['M8 3h8a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z', 'M11 18h2']);
/**
 * Swish: a payment leaving a phone. Not the Swish word mark — this set is
 * monochrome line art on a 24 px grid, and an approximation of a trademarked
 * logo would look like neither. The old two-arc swirl read as "uppdatera".
 */
export const SwishIcon = make('swish', [
  'M4 3h8.5a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z',
  'M7 18h2.5',
  'M15.5 14.5 21 9',
  'M16 9h5v5',
]);
export const QrIcon = make('qr', ['M4 4h6v6H4z', 'M14 4h6v6h-6z', 'M4 14h6v6H4z', 'M14 14h2v2h-2z', 'M18 14h2v2h-2z', 'M14 18h2v2h-2z', 'M18 18h2v2h-2z', 'M6.5 6.5h1v1h-1z', 'M16.5 6.5h1v1h-1z', 'M6.5 16.5h1v1h-1z']);
export const KeyIcon = make('key', ['M15 14a5 5 0 1 0-4.6-3H3v3h2v2h3v-2h2.4A5 5 0 0 0 15 14z', 'M16 9h.01']);
export const FileIcon = make('file', ['M7 3h7l5 5v13H7z', 'M14 3v5h5']);
export const ImageIcon = make('image', ['M4 5h16v14H4z', 'M9 10a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z', 'm4 17 5-5 4 4 3-3 4 4']);
export const PaperclipIcon = make('paperclip', ['m16 8-6.5 6.5a2 2 0 0 0 2.8 2.8L19 10.6a4 4 0 0 0-5.7-5.7L6.6 11.6a6 6 0 0 0 8.5 8.5L20 15']);
export const BookIcon = make('book', ['M5 4h6a2 2 0 0 1 2 2v14a2 2 0 0 0-2-2H5z', 'M19 4h-6a2 2 0 0 0-2 2v14a2 2 0 0 1 2-2h6z']);
export const TagIcon = make('tag', ['M4 4h7l9 9-7 7-9-9z', 'M8 8h.01']);
export const CalendarIcon = make('calendar', ['M4 6h16v14H4z', 'M4 10h16', 'M8 3v4', 'M16 3v4']);
export const PersonIcon = make('person', ['M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M4 21a8 8 0 0 1 16 0']);
export const StarIcon = make('star', ['m12 3 2.7 5.8 6.3.7-4.7 4.3 1.3 6.2L12 16.9 6.4 20l1.3-6.2L3 9.5l6.3-.7z']);
export const KeyboardIcon = make('keyboard', ['M3 7h18v10H3z', 'M7 11h.01', 'M11 11h.01', 'M15 11h.01', 'M8 14h8']);

// Actions
export const SearchIcon = make('search', ['M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14z', 'm20 20-4-4']);
export const SettingsIcon = make('settings', ['M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z', 'M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z']);
export const PencilIcon = make('pencil', ['M4 20h4l11-11-4-4L4 16z', 'm13 7 4 4']);
export const TrashIcon = make('trash', ['M5 7h14', 'M9 7V4h6v3', 'M7 7l1 13h8l1-13', 'M10 11v5', 'M14 11v5']);
export const CopyIcon = make('copy', ['M9 9h11v11H9z', 'M5 15V4h11']);
export const PrinterIcon = make('printer', ['M7 8V3h10v5', 'M5 8h14a2 2 0 0 1 2 2v6h-4v-3H7v3H3v-6a2 2 0 0 1 2-2z', 'M7 13h10v8H7z']);
export const EyeIcon = make('eye', ['M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z', 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z']);
export const EyeOffIcon = make('eye-off', ['M4 4l16 16', 'M9.9 5.1A10 10 0 0 1 12 5c6 0 9.5 7 9.5 7a17 17 0 0 1-3 3.9', 'M6.1 6.9A17 17 0 0 0 2.5 12S6 19 12 19a9.6 9.6 0 0 0 4.4-1.1', 'M9.9 9.9a3 3 0 0 0 4.2 4.2']);
export const FilterIcon = make('filter', ['M4 5h16l-6 8v6l-4-2v-4z']);
export const LinkIcon = make('link', ['M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 1 0-5.7-5.7l-1 1', 'M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 1 0 5.7 5.7l1-1']);
export const ZoomInIcon = make('zoom-in', ['M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14z', 'm20 20-4-4', 'M11 8v6', 'M8 11h6']);
export const ZoomOutIcon = make('zoom-out', ['M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14z', 'm20 20-4-4', 'M8 11h6']);
/* The page below, the turn above: a ring with an arrowhead alone reads as
   "reload". */
const ROTATE_PAGE = 'M8 13.5h8a1.5 1.5 0 0 1 1.5 1.5v4a1.5 1.5 0 0 1-1.5 1.5H8a1.5 1.5 0 0 1-1.5-1.5v-4A1.5 1.5 0 0 1 8 13.5z';
export const RotateRightIcon = make('rotate-right', ['M5 10.5a7 7 0 0 1 14 0', 'm16.5 8 2.5 2.5L21.5 8', ROTATE_PAGE]);
export const RotateLeftIcon = make('rotate-left', ['M19 10.5a7 7 0 0 0-14 0', 'M7.5 8 5 10.5 2.5 8', ROTATE_PAGE]);
export const SplitIcon = make('split', ['M12 4v16', 'M4 9h5', 'M15 9h5', 'M4 15h5', 'M15 15h5']);
export const UndoIcon = make('undo', ['M9 14 4 9l5-5', 'M4 9h10a6 6 0 0 1 0 12h-3']);
export const FlagIcon = make('flag', ['M5 21V4', 'M5 4h13l-3 4.5 3 4.5H5']);
export const ShieldIcon = make('shield', ['M12 3 5 6v6c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6z', 'm9 12 2 2 4-4']);
export const HistoryIcon = make('history', ['M4 12a8 8 0 1 0 2.3-5.7', 'M4 4v4.5h4.5', 'M12 8v4l3 2']);

/** Kind icons for payment sources. */
export function paymentSourceIcon(kind: 'bank' | 'card' | 'swish' | 'cash' | 'owner' | 'supplier_credit' | 'customer_credit' | 'tax_account'): IconComponent {
  switch (kind) {
    case 'bank':
      return BankIcon;
    case 'card':
      return CardIcon;
    case 'swish':
      return SwishIcon;
    case 'cash':
      return CashIcon;
    case 'owner':
      return PersonIcon;
    case 'supplier_credit':
    case 'customer_credit':
      return FileIcon;
    case 'tax_account':
      return BuildingIcon;
    default: {
      const exhaustive: never = kind;
      throw new Error(`Unknown payment source kind ${String(exhaustive)}`);
    }
  }
}
